"use client";

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { StarIcon } from '@heroicons/react/24/solid';
import {
  AdjustmentsHorizontalIcon,
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  SparklesIcon,
  StarIcon as StarOutlineIcon,
} from '@heroicons/react/24/outline';
import Modal from './Modal';
import WebRegImport from './WebRegImport';
import type { WebRegImportReceipt } from '../scheduling/useWebRegScheduleImport';
import type { BlankScheduleCreationReceipt } from '../scheduling/useBlankScheduleCreation';
import { formatScheduleUpdatedAt } from '@/app/lib/scheduleUpdatedAt';
import { SEMESTER_OPTIONS } from '@/app/lib/semesters';

interface Schedule {
  schedule_id: string;
  name: string;
  updated_at: string;
  term_id?: string;
  is_primary?: boolean;
}

interface ScheduleSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  schedules: Schedule[];
  /** False marks an obsolete/cancelled operation and must not close the dialog. */
  onScheduleSelect: (scheduleId: string, review?: BlankScheduleCreationReceipt) => void | false | Promise<void | false>;
  primaryScheduleId?: string;
  isViewingSchedule?: boolean;
  onWebRegImport?: (courses: Array<{ baseId: string; sectionId: string }>, scheduleName: string, review: WebRegImportReceipt) => void | false | Promise<void | boolean>;
  currentSemester?: string;
  /** Owner and route identity supplied by the host; invalidates old selection work. */
  operationContextKey?: string;
  isLoadingSchedules?: boolean;
  schedulesLoadError?: string | null;
  onRetrySchedules?: () => void;
}

const ScheduleSelectionModal: React.FC<ScheduleSelectionModalProps> = ({
  isOpen,
  onClose,
  schedules,
  onScheduleSelect,
  primaryScheduleId,
  isViewingSchedule = false,
  onWebRegImport,
  currentSemester = 'fall-2026',
  operationContextKey = '',
  isLoadingSchedules = false,
  schedulesLoadError = null,
  onRetrySchedules,
}) => {
  const [selectedSemester, setSelectedSemester] = useState<string>('all');
  const [showWebRegImport, setShowWebRegImport] = useState(false);
  const [pendingScheduleId, setPendingScheduleId] = useState<string | null>(null);
  const [selectionError, setSelectionError] = useState<string | null>(null);
  const selectionPendingRef = useRef<symbol | null>(null);
  const dialogEpochRef = useRef<symbol | null>(null);
  const isSelecting = pendingScheduleId !== null;
  const importTriggerRef = useRef<HTMLButtonElement>(null);
  const importRegionRef = useRef<HTMLDivElement>(null);
  const previouslyShowingImport = useRef(false);
  const importContext = useMemo(() => ({ isOpen, showWebRegImport, currentSemester, operationContextKey }), [isOpen, showWebRegImport, currentSemester, operationContextKey]);
  const committedImportContext = useRef<typeof importContext | null>(null);
  useLayoutEffect(() => {
    committedImportContext.current = importContext;
    return () => { if (committedImportContext.current === importContext) committedImportContext.current = null; };
  }, [importContext]);
  const directoryUnavailable = isLoadingSchedules || Boolean(schedulesLoadError);

  // A closing/reopened dialog or replacement owner/term/route starts a new
  // lifetime. Layout cleanup invalidates old promises before the next paint.
  useLayoutEffect(() => {
    const epoch = isOpen ? Symbol('schedule-dialog') : null;
    dialogEpochRef.current = epoch;
    selectionPendingRef.current = null;
    setPendingScheduleId(null);
    setSelectionError(null);
    return () => {
      if (dialogEpochRef.current === epoch) {
        dialogEpochRef.current = null;
        selectionPendingRef.current = null;
      }
    };
  }, [isOpen, currentSemester, operationContextKey]);

  useEffect(() => {
    if (!isOpen) { previouslyShowingImport.current = false; return; }
    if (showWebRegImport) {
      const target = importRegionRef.current?.querySelector<HTMLElement>('input[type="file"]') ?? importRegionRef.current?.querySelector<HTMLElement>('button');
      target?.focus();
    } else if (previouslyShowingImport.current) {
      importTriggerRef.current?.focus();
    }
    previouslyShowingImport.current = showWebRegImport;
  }, [isOpen, showWebRegImport]);

  // Filter schedules by semester
  const filteredSchedules = schedules.filter(schedule => {
    if (selectedSemester === 'all') return true;
    const knownTerm = SEMESTER_OPTIONS.some((term) => term.id === schedule.term_id);
    if (selectedSemester === 'unspecified') return !knownTerm;
    return schedule.term_id === selectedSemester;
  });

  // Sort schedules: primary first, then by updated_at descending
  const sortedSchedules = [...filteredSchedules].sort((a, b) => {
    // Primary schedule always comes first
    if (a.schedule_id === primaryScheduleId) return -1;
    if (b.schedule_id === primaryScheduleId) return 1;

    // Then sort by updated_at descending
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  const handleScheduleClick = async (scheduleId: string) => {
    const epoch = dialogEpochRef.current;
    if (!epoch || selectionPendingRef.current || directoryUnavailable) return;
    const operation = Symbol('schedule-selection');
    selectionPendingRef.current = operation;
    const isCurrent = () => dialogEpochRef.current === epoch && selectionPendingRef.current === operation;
    setPendingScheduleId(scheduleId);
    setSelectionError(null);
    try {
      const selected = scheduleId === 'new'
        ? await onScheduleSelect(scheduleId, { identity: { epoch, operation }, isCurrent: () => isCurrent()
          && committedImportContext.current === importContext && !importContext.showWebRegImport })
        : await onScheduleSelect(scheduleId);
      if (selected !== false && isCurrent()) onClose();
    } catch (error) {
      if (isCurrent()) setSelectionError(error instanceof Error ? error.message : 'Unable to open this schedule. Please try again.');
    } finally {
      if (isCurrent()) {
        selectionPendingRef.current = null;
        setPendingScheduleId(null);
      }
    }
  };

  const handleOpenInNewTab = (scheduleId: string) => {
    window.open(`/scheduling?id=${scheduleId}`, '_blank');
  };

  // Handle X button click - only open primary schedule if user is not already viewing a schedule
  const handleClose = () => {
    if (selectionPendingRef.current) return;
    if (directoryUnavailable) {
      onClose();
      return;
    }
    if (showWebRegImport) {
      setShowWebRegImport(false);
      return;
    }
    if (!isViewingSchedule && schedules.length > 0 && primaryScheduleId) {
      void handleScheduleClick(primaryScheduleId);
      return;
    }
    onClose();
  };

  const handleWebRegImport = async (courses: Array<{ baseId: string; sectionId: string }>, scheduleName: string, review: WebRegImportReceipt): Promise<boolean> => {
    const epoch = dialogEpochRef.current;
    if (!onWebRegImport || !epoch || !importContext.isOpen || !importContext.showWebRegImport
      || committedImportContext.current !== importContext || selectionPendingRef.current) return false;
    const operation = Symbol('webreg-modal-import');
    selectionPendingRef.current = operation;
    const isCurrent = () => dialogEpochRef.current === epoch && committedImportContext.current === importContext
      && selectionPendingRef.current === operation && review.isCurrent();
    try {
      const imported = await onWebRegImport(courses, scheduleName, { identity: review.identity, isCurrent });
      if (imported === false || !isCurrent()) return false;
      setShowWebRegImport(false);
      onClose();
      return true;
    } finally {
      if (selectionPendingRef.current === operation) selectionPendingRef.current = null;
    }
  };
  const currentSemesterLabel = currentSemester
    .split('-')
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

  const getSemesterBadge = (termId?: string) => {
    const semester = termId;
    if (!semester) {
      return <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">Term not recorded</span>;
    }
    if (semester === 'fall-2025') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200">
          Fall 2025
        </span>
      );
    } else if (semester === 'spring-2026') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200">
          Spring 2026
        </span>
      );
    } else if (semester === 'fall-2026') {
      return (
        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
          Fall 2026
        </span>
      );
    }
    const supported = SEMESTER_OPTIONS.find((term) => term.id === semester);
    return <span className="inline-flex items-center rounded-full bg-slate-100 px-2 py-1 text-xs font-medium text-slate-700 dark:bg-slate-700 dark:text-slate-200">{supported?.label ?? 'Other term'}</span>;
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={directoryUnavailable && !showWebRegImport ? 'Select a Schedule' : showWebRegImport ? 'Import from WebReg' : schedules.length ? 'Select a Schedule' : 'Create your first schedule'}
      size="lg"
    >
      {directoryUnavailable && !showWebRegImport ? (
        <div className="space-y-4" aria-busy={isLoadingSchedules}>
          {isLoadingSchedules ? (
            <p role="status" className="text-sm text-[var(--ss-text-muted)]">Loading your schedules…</p>
          ) : (
            <>
              <p role="alert" className="text-sm text-[var(--ss-critical)]">Your schedules could not be loaded.</p>
              {onRetrySchedules && <button type="button" onClick={onRetrySchedules} className="ss-button min-h-12" data-variant="secondary">Retry</button>}
            </>
          )}
        </div>
      ) : showWebRegImport ? (
        <div ref={importRegionRef}>
          <WebRegImport
            key={JSON.stringify([operationContextKey, currentSemester])}
            onImport={handleWebRegImport}
            onClose={() => setShowWebRegImport(false)}
            semester={currentSemester}
            existingScheduleCount={schedules.length}
          />
        </div>
      ) : (
        <div className="space-y-4" aria-busy={isSelecting}>
          {selectionError && <p role="alert" className="text-sm font-medium text-[var(--ss-critical)]">{selectionError}</p>}
          {isSelecting && <p role="status" className="text-sm text-[var(--ss-text-muted)]">{pendingScheduleId === 'new' ? 'Creating your schedule…' : 'Opening your schedule…'}</p>}
          {schedules.length > 0 ? (
            <>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-slate-600 dark:text-slate-400 text-sm">
                  Your primary schedule is starred and appears first.
                </p>
                <select
                  aria-label="Filter saved schedules by semester"
                  value={selectedSemester}
                  disabled={isSelecting}
                  onChange={(e) => setSelectedSemester(e.target.value)}
                  className="min-h-12 shrink-0 px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-control bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                >
                  <option value="all">All Semesters</option>
                  {SEMESTER_OPTIONS.map((term) => <option key={term.id} value={term.id}>{term.label}</option>)}
                  <option value="unspecified">Unspecified or other term</option>
                </select>
              </div>

              <div className="max-h-96 overflow-y-auto space-y-2">
                {sortedSchedules.map((schedule) => (
                  <div
                    key={schedule.schedule_id}
                    className="flex items-center gap-2 p-2 bg-slate-50 dark:bg-slate-700 rounded-control border border-slate-200 dark:border-slate-600 group"
                  >
                    <button
                      type="button"
                      aria-label={`Open schedule ${schedule.name}`}
                      onClick={() => void handleScheduleClick(schedule.schedule_id)}
                      disabled={isSelecting}
                      className="flex min-h-12 min-w-0 flex-1 items-center gap-3 rounded-control p-2 text-left hover:bg-slate-100 dark:hover:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                    >
                      {schedule.schedule_id === primaryScheduleId ? (
                        <StarIcon className="w-5 h-5 text-yellow-500 flex-shrink-0" />
                      ) : (
                        <StarOutlineIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                      )}
                      <span className="flex-1 min-w-0">
                        <span className="flex flex-wrap items-center gap-2 mb-1">
                          <span className="font-semibold text-slate-900 dark:text-white truncate">
                            {schedule.name}
                          </span>
                          {getSemesterBadge(schedule.term_id)}
                        </span>
                        <span className="block text-sm text-slate-600 dark:text-slate-300">
                          {formatScheduleUpdatedAt(schedule.updated_at) ? `Updated ${formatScheduleUpdatedAt(schedule.updated_at)}` : 'Update date unavailable'}
                        </span>
                      </span>
                    </button>

                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        aria-label={`Open schedule ${schedule.name} in a new tab`}
                        disabled={isSelecting}
                        onClick={() => handleOpenInNewTab(schedule.schedule_id)}
                        className="flex min-h-12 min-w-12 items-center justify-center p-2 text-slate-600 dark:text-slate-300 rounded-control hover:bg-slate-200 dark:hover:bg-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] md:opacity-0 md:group-hover:opacity-100 md:group-focus-within:opacity-100 focus-visible:opacity-100"
                        title="Open in new tab"
                      >
                        <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              <div className="pt-4 border-t border-slate-200 dark:border-slate-600 space-y-2">
                <button
                  onClick={() => void handleScheduleClick('new')}
                  disabled={isSelecting}
                  className="min-h-12 w-full px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-control transition-colors font-medium flex items-center justify-center space-x-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] focus-visible:ring-offset-2"
                >
                  <PlusIcon className="w-5 h-5" />
                  <span>{pendingScheduleId === 'new' ? 'Creating schedule…' : 'Create New Schedule'}</span>
                </button>
                {onWebRegImport && (
                  <button
                    ref={importTriggerRef}
                    disabled={isSelecting}
                    onClick={() => setShowWebRegImport(true)}
                    className="min-h-12 w-full px-4 py-3 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 text-slate-700 dark:text-slate-200 rounded-control transition-colors font-medium flex items-center justify-center space-x-2 border border-slate-300 dark:border-slate-600 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)]"
                  >
                    <ArrowUpTrayIcon className="w-5 h-5" />
                    <span>Import from WebReg</span>
                  </button>
                )}
              </div>
            </>
          ) : (
            <section aria-labelledby="first-schedule-heading" className="py-2 text-center sm:py-4">
              <span className="inline-flex rounded-control bg-[var(--ss-brand-bg)] px-2.5 py-1 text-xs font-semibold text-[var(--ss-focus)]">
                {currentSemesterLabel}
              </span>
              <h3 id="first-schedule-heading" className="mt-4 text-2xl font-bold text-[var(--ss-text)]">
                Build a week that works for you
              </h3>
              <p className="mx-auto mt-2 max-w-xl text-sm leading-6 text-[var(--ss-text-muted)]">
                Start with a blank schedule or bring in your current WebReg sections. We&apos;ll guide you through the rest.
              </p>

              <ol className="mt-6 grid gap-3 text-left sm:grid-cols-3">
                {[
                  [MagnifyingGlassIcon, 'Add courses', 'Search by course name or code.'],
                  [AdjustmentsHorizontalIcon, 'Set preferences', 'Tell us when and where you prefer class.'],
                  [SparklesIcon, 'Compare schedules', 'Review generated options side by side.'],
                ].map(([Icon, title, detail], index) => {
                  const StepIcon = Icon as typeof MagnifyingGlassIcon;
                  return (
                    <li key={title as string} className="rounded-card border border-[var(--ss-border)] bg-[var(--ss-surface-subtle)] p-3">
                      <div className="flex items-center gap-2 text-[var(--ss-focus)]">
                        <StepIcon className="h-5 w-5" aria-hidden="true" />
                        <span className="text-xs font-semibold uppercase tracking-wide">Step {index + 1}</span>
                      </div>
                      <p className="mt-2 text-sm font-semibold text-[var(--ss-text)]">{title as string}</p>
                      <p className="mt-1 text-xs leading-5 text-[var(--ss-text-muted)]">{detail as string}</p>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-6 space-y-3">
                <button
                  onClick={() => void handleScheduleClick('new')}
                  disabled={isSelecting}
                  className="flex min-h-12 w-full items-center justify-center gap-2 rounded-control bg-[var(--ss-brand)] px-6 py-3 text-base font-semibold text-white transition-colors hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none"
                >
                  <PlusIcon className="h-5 w-5" aria-hidden="true" />
                  {pendingScheduleId === 'new' ? 'Creating schedule…' : `Create my ${currentSemesterLabel} schedule`}
                </button>
                {onWebRegImport && (
                  <button
                    ref={importTriggerRef}
                    disabled={isSelecting}
                    onClick={() => setShowWebRegImport(true)}
                    className="flex min-h-12 w-full items-center justify-center gap-2 rounded-control border border-[var(--ss-border)] bg-[var(--ss-surface)] px-6 py-3 font-semibold text-[var(--ss-text)] transition-colors hover:bg-[var(--ss-surface-subtle)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ss-focus)] motion-reduce:transition-none"
                  >
                    <ArrowUpTrayIcon className="w-5 h-5" />
                    <span>Import my WebReg schedule</span>
                  </button>
                )}
              </div>
              <p className="mt-4 text-xs text-[var(--ss-text-muted)]">Nothing is final—you can change courses, sections, and preferences anytime.</p>
            </section>
          )}
        </div>
      )}
    </Modal>
  );
};

export default ScheduleSelectionModal;
